import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { roles } from '../../utils/roles';
import { getPermissions } from '../../lib/accessControl';
import { abortController } from '../../utils/abortController';
import withAuth from '../../lib/withAuth';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import usePageFilterPersistence from '../../hooks/usePageFilterPersistence';
import MaterialReactDataTable from '../../components/MaterialReactDataTable';
import Backdrop from '@mui/material/Backdrop';
import Alert from '../../components/Alert';
import Modal from 'react-bootstrap/Modal';
import Loader from '../../components/Loader';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import axios from 'axios';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { BiReset } from 'react-icons/bi';
import ButtonGroup from '@mui/material/ButtonGroup';
import DocumentCommentsSection from './DocumentCommentsSection';
import VirtualizedAutocomplete from '../../components/Autocomplete';

const ApproveDocuments = ({ logOut = () => { } }) => {


    /* AUTHORIZATION VARAIBLES */
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const { deal_no } = useParams();

    /* AUTHENTICATION VARIABLES */
    const currentUser = useUser();

    /* AUTHORIZATION VARIABLES */
    const { isWritable = false } = getPermissions(currentUser?.role, pathname);
    const isDocumentTeamRole = currentUser?.role === roles[2];
    const isLCTeamRole = currentUser?.role === roles[3];
    
    const businessUnits = getPermissions(currentUser?.role, null, "businessUnits");

    /* SESSION EXPIRY VARIABLES */
    const triggerSessionExpire = useSessionExpire();

    /* PERSISTED FILTERS (redux, keyed by route) */
    const pageKey = `${pathname}::ApproveDocuments`;
    const {
        filterValues: persistedFilters,
        setFilter: persistFilter,
        setFilters: persistFilters
    } = usePageFilterPersistence(pageKey);
    const hasHydratedFiltersRef = useRef(false);
    const globalFilterDebounceRef = useRef(null);

    /* API VARIABLES */
    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("");

    /* DATATABLE VARIABLES */
    const [documentData, setDocumentData] = useState([]);
    const [toggleDisplayData, setToggleDisplayData] = useState(false);
    const tableRef = useRef();

    /* DATATABLE ROW DATA VARIABLES */
    const [rowData, setRowData] = useState({});
    const [rowCommentData, setRowCommentData] = useState({});

    /* CONDITIONAL FILTER VARIABLES */
    const [isFilterable, setIsFilterable] = useState(true);
    const [filter, setFilter] = useState(true);


    /* DATATABLE GLOBAL FILTER VARIABLES */
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    /* TAB VARIABLES */
    const [selectedTab, setSelectedTab] = useState(0);
    const [isTabChanged, setIsTabChanged] = useState(false);

    /* MODAL VARIABLES */
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);

    // ENTITY VARIABLES 
    const [entitiesForCopy, setEntitiesForCopy] = useState([]);
    const [isEntitiesLoading, setIsEntitiesLoading] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState(null);


    // FILTERS
    const paymentOptions = useMemo(() => [
        'No',
        'Yes',
    ], []);
    const [selectedPaymentOption, setSelectedPaymentOption] = useState(paymentOptions[0]);
    const documentStatusOptions = useMemo(() => [
        'Both',
        'Not Approved',
        'Approved'
    ], []);
    const [selectedDocumentStatus, setSelectedDocumentStatus] = useState(documentStatusOptions[0]);

    const isFirstRender = useRef(true);



    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return; // Skip the effect on the first render
        }

        if (selectedEntity || selectedPaymentOption || selectedDocumentStatus) {
            setIsFilterable(true);
        } else {
            setIsFilterable(false);
        }
    }, [selectedEntity, selectedPaymentOption, selectedDocumentStatus]);

    const sendAllDocumentsApprovedNotification = useCallback(async ({ deal_pfi_id, user_id, Business_Unit_Id }) => {
        try {
            await axios({
                method: "post",
                url: "/api/AllDocumentsApprovedNotification",
                data: {
                    deal_pfi_id,
                    user_id,
                    Business_Unit_Id
                },
                signal: abortController.signal
            });
        } catch (error) {
            console.log("All Documents Approved Notification Api: ", error);
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access."
                        : status === 429
                            ? "Request limit exceeded."
                            : "An error occurred while sending notification."
                );
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        }
    }, [triggerSessionExpire, logOut]);

    useEffect(() => {
        const fetchEntitiesList = async () => {

            setSelectedEntity(null);
            setIsEntitiesLoading(true);

            try {
                const response = await axios({
                    method: "post",
                    url: "/api/GetEntity",
                    data: {
                        business_unit_id: "",
                    },
                    signal: abortController.signal
                });

                if (response.status === 200) {
                    const apiEntities = response.data?.data || [];

                    setEntitiesForCopy([
                        ...apiEntities,
                        { id: "", value: "Both" }
                    ]); // adjust based on actual response shape
                } else {
                    setAlertMessage("Failed to load entities.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            } catch (error) {
                if (axios.isCancel(error)) return;
                console.error("GetDealListEntity API error:", error);
                setAlertMessage("Error loading entities for copy.");
                setAlertType("error");
                setShowAlert(true);
            } finally {
                setIsEntitiesLoading(false);
            }
        }

        fetchEntitiesList();
    }, [])


    useEffect(() => {
        const fetchData = async () => {
            const entityId = selectedEntity?.id || null;
            setIsLoading(true);
            setShowAlert(false);
            setAlertMessage("");
            setAlertType("");
            setRowData({});

            await axios({
                method: "post",
                url: "/api/GetDocumentData",
                data: {
                    selected_deal_type: selectedTab ? businessUnits[selectedTab - 1]?.id : "All",
                    filter_value: globalFilterValue !== "" ? globalFilterValue : deal_no ? deal_no : "",
                    lc_payment: selectedPaymentOption || null,
                    entity_id: entityId,
                    document_status: selectedDocumentStatus || 'Not Approved'
                },
                signal: abortController.signal
            })
                .then((response) => {
                    setIsLoading(false);
                    const { status, data } = response;

                    if (status === 200) {
                        const documentData = data?.document_data || {};
                        setDocumentData(documentData);
                        console.log("Document Data: ", documentData);
                    } else {
                        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                        setAlertType("error");
                        setShowAlert(true);
                    }
                })
                .catch((error) => {
                    console.log("Get Document Data Api: ", error);
                    setIsLoading(false);
                    if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                    const status = error?.response?.status;
                    if (status === 403) {
                        triggerSessionExpire();
                    } else {
                        setAlertMessage(
                            status === 401
                                ? "Unauthorized access."
                                : status === 429
                                    ? "Request limit exceeded."
                                    : "An error occurred while processing your request."
                        );
                        setAlertType("error");
                        setShowAlert(true);
                        if (status === 429) {
                            setTimeout(logOut, 3000);
                        }
                    }
                });
        }

        if (businessUnits?.length > 0) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, businessUnits, selectedTab, toggleDisplayData, pathname]);

    /* -------- PERSISTED FILTERS: hydrate on mount, and force a single
       auto re-fetch if we land on the page with any persisted filter value
       already set (entity, LC payment, document status, or global search).
       A persisted tab change already re-triggers GetDocumentData on its own
       via the effect's `selectedTab` dependency above, but the other
       filters aren't dependencies of that effect, so we nudge it via
       toggleDisplayData instead. Hydration and this decision are read
       straight from persistedFilters (not component state) to avoid a race
       with state updates not yet being reflected in a re-render. -------- */
    useEffect(() => {
        if (hasHydratedFiltersRef.current) return;
        hasHydratedFiltersRef.current = true;

        if (!persistedFilters) return;

        const {
            selectedEntity: persistedEntity,
            selectedPaymentOption: persistedPaymentOption,
            selectedDocumentStatus: persistedDocumentStatus,
            selectedTab: persistedTab,
            globalFilterValue: persistedGlobalFilterValue
        } = persistedFilters;

        if (persistedEntity) setSelectedEntity(persistedEntity);
        if (persistedPaymentOption) setSelectedPaymentOption(persistedPaymentOption);
        if (persistedDocumentStatus) setSelectedDocumentStatus(persistedDocumentStatus);
        if (typeof persistedTab === 'number') setSelectedTab(persistedTab);
        if (persistedGlobalFilterValue) setGlobalFilterValue(persistedGlobalFilterValue);

        const hadPersistedFilter =
            persistedEntity ||
            (persistedPaymentOption && persistedPaymentOption !== paymentOptions[0]) ||
            (persistedDocumentStatus && persistedDocumentStatus !== documentStatusOptions[0]) ||
            persistedGlobalFilterValue;

        if (hadPersistedFilter) {
            setIsFilterable(true);
            setFilter(true);
            setToggleDisplayData((previous) => !previous);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    /* -------- PERSISTED FILTERS: write entity/payment/status/tab back to
       redux immediately on change -------- */
    useEffect(() => {
        if (!hasHydratedFiltersRef.current) return;

        persistFilters({
            selectedEntity: selectedEntity || null,
            selectedPaymentOption,
            selectedDocumentStatus,
            selectedTab
        });
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [selectedEntity, selectedPaymentOption, selectedDocumentStatus, selectedTab]);

    /* -------- PERSISTED FILTERS: write global search value (debounced) -------- */
    useEffect(() => {
        if (!hasHydratedFiltersRef.current) return;

        if (globalFilterDebounceRef.current) clearTimeout(globalFilterDebounceRef.current);

        globalFilterDebounceRef.current = setTimeout(() => {
            persistFilter('globalFilterValue', globalFilterValue || "");
        }, 400);

        return () => clearTimeout(globalFilterDebounceRef.current);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [globalFilterValue]);

    const handleTabChange = (tab) => {
        setIsTabChanged(selectedTab !== tab);
        setSelectedTab(tab);
    }

    const handleViewDocument = async (rowData) => {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const {
            deal_pfi_id: Deal_PFI_Id,
            tranche_id: Tranche_Id,
            coo_id: COO_ID,
            external_file_id: External_File_Id,
            revision_number: Revision_Number,
            isApproved: is_approved_document,
            "Document Type": Document_Type,
            is_external_document: isExternalDocument
        } = rowData || {};

        const documentRowData = {
            pathname: pathname,
            Deal_PFI_Id,
            Tranche_Id,
            COO_ID,
            External_File_Id,
            Revision_Number,
            Document_Type: isExternalDocument ? "External Document" : Document_Type,
            is_approved_document,
            view_document: true
        };

        await axios({
            method: "post",
            url: "/api/ViewDocument",
            data: documentRowData,
            signal: abortController.signal
        })
            .then((response) => {
                setIsLoading(false);
                const { status, data } = response;

                if (status === 200) {
                    window.open(data, "_blank", "noopener, noreferrer");
                } else if (status === 202) {
                    setAlertMessage(data?.data);
                    setAlertType("info");
                    setShowAlert(true);
                } else {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch((error) => {
                console.log("View Document Api: ", error);
                setIsLoading(false);
                if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                const status = error?.response?.status;
                if (status === 403) {
                    triggerSessionExpire();
                } else {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access."
                            : status === 429
                                ? "Request limit exceeded."
                                : "An error occurred while processing your request."
                    );
                    setAlertType("error");
                    setShowAlert(true);
                    if (status === 429) {
                        setTimeout(logOut, 3000);
                    }
                }
            });
    }

    const handleAddOrEditComment = async (rowData) => {
        const {
            deal_pfi_id: Deal_PFI_Id,
            tranche_id: Tranche_Id,
            coo_id: COO_ID,
            external_file_id: External_File_Id,
            revision_number: Revision_Number,
            "Document Type": Document_Type,
            Comments,
            is_external_document: isExternalDocument,
            Business_Unit_Id,
            isPFIDocument
        } = rowData || {};

        const rowCommentData = {
            ...rowData,
            isPFIDocument,
            Business_Unit_Id,
            Deal_PFI_Id,
            Tranche_Id,
            COO_ID,
            External_File_Id,
            Revision_Number,
            Document_Type: isExternalDocument ? "External Document" : Document_Type,
            Comments
        };
        setRowCommentData(rowCommentData);
        setShowCommentModal(true);
    };

    const handleRecordStatusChangeWrapper = (rowData) => {
        setRowData(rowData);
        if (rowData?.isApproved || rowData?.isDeleted) {
            setShowDisableModal(true);
        } else {
            handleRecordStatusChange(rowData);
        }
    }

    const handleRecordStatusChange = useCallback(async (rowData) => {
        setShowDisableModal(false);
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");
        setShowCommentModal(false);

        const {
            Business_Unit_Id,
            Status,
            isPFIDocument,
            deal_pfi_id: Deal_PFI_Id,
            tranche_id: Tranche_Id,
            coo_id: COO_ID,
            external_file_id: External_File_Id,
            revision_number: Revision_Number,
            "Document Type": Document_Type,
            "Document Name": Document_Name,
            "Reviewed By": Reviewed_By,
            isApproved,
            isDeleted: Access_Status,
            is_external_document: isExternalDocument
        } = rowData || {};

        const documentRowData = {
            Status,
            Business_Unit_Id,
            isPFIDocument,
            Deal_PFI_Id,
            Tranche_Id,
            COO_ID,
            External_File_Id,
            Revision_Number,
            Document_Type: isExternalDocument ? "External Document" : Document_Type,
            Document_Name,
            Document_Status: isApproved ? 0 : 1,
            Access_Status,
            Reviewed_By,
            User_Id: currentUser?.id,
        };

        try {
            if (rowData?.isApproved && !isPFIDocument) {
                // Call both APIs when isApproved is true and isPFIDocument is false
                const setDocumentStatusResponse = await axios({
                    method: "post",
                    url: "/api/SetDocumentStatus",
                    data: documentRowData,
                    signal: abortController.signal
                });

                const markStatusDealPFIResponse = await axios({
                    method: "post",
                    url: "/api/MarkStatusDealPFIDocument",
                    data: { ...documentRowData, Status: null },
                    signal: abortController.signal
                });

                setIsLoading(false);

                if (setDocumentStatusResponse.status === 200 && markStatusDealPFIResponse.status === 200) {
                    setToggleDisplayData(!toggleDisplayData);
                } else {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            } else {
                // Original else logic remains unchanged
                let apiUrl = documentRowData?.isPFIDocument ? "/api/MarkStatusDealPFIDocument" : "/api/SetDocumentStatus";

                const response = await axios({
                    method: "post",
                    url: apiUrl,
                    data: documentRowData,
                    signal: abortController.signal
                });

                setIsLoading(false);
                const { status } = response;

                if (status === 200) {
                    setToggleDisplayData(!toggleDisplayData);
                } else {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            }
        } catch (error) {
            console.log("Set Document Status Api: ", error);
            setIsLoading(false);
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access."
                        : status === 429
                            ? "Request limit exceeded."
                            : "An error occurred while processing your request."
                );
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, sendAllDocumentsApprovedNotification]);

    const handleGlobalFilter = async (table = null, rowData) => {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        try {
            const response = await axios({
                method: "post",
                url: "/api/FilterTableData",
                data: {
                    pathname: pathname,
                    table_data: rowData,
                    filter_value: globalFilterValue,
                    lc_payment: selectedPaymentOption || null,
                    document_status: selectedDocumentStatus || 'Not Approved'
                },
                signal: abortController.signal,
            });

            setIsLoading(false);
            const { status, data } = response;

            if (status === 200) {
                if (table?.resetRowSelection) {
                    table.resetRowSelection();
                }
                if (tableRef?.current?.setFilteredData) {
                    tableRef.current.setFilteredData(data || []);
                }
            } else {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
            }
        } catch (error) {
            console.log("Filter Table Data Api: ", error);
            setIsLoading(false);
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access."
                        : status === 429
                            ? "Request limit exceeded."
                            : "An error occurred while processing your request."
                );
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        }
    };

    const handleBulkViewOrDownload = async (selectedRows, actionType) => {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const documentRowData = selectedRows?.map(({
            deal_pfi_id: Deal_PFI_Id,
            tranche_id: Tranche_Id,
            coo_id: COO_ID,
            external_file_id: External_File_Id,
            revision_number: Revision_Number,
            isApproved: is_approved_document,
            "Document Type": Document_Type,
            is_external_document: isExternalDocument
        }) => ({
            Deal_PFI_Id,
            Tranche_Id,
            COO_ID,
            External_File_Id,
            Revision_Number,
            Document_Type: isExternalDocument ? "External Document" : Document_Type,
            is_approved_document,
            view_document: actionType === "view"
        }));

        await axios({
            method: "post",
            url: "/api/ViewDocuments",
            data: documentRowData,
            signal: abortController.signal
        })
            .then((response) => {
                setIsLoading(false);
                const { status, data } = response;

                if (status === 200) {
                    const { file_urls: fileUrls = [], unavailable_files: unavailableFiles = [] } = data;
                    fileUrls.forEach((url) => {
                        window.open(url, "_blank", "noopener, noreferrer");
                    });
                    if (unavailableFiles.length > 0) {
                        setAlertMessage(`No file is available for the documents: ${unavailableFiles.join(", ")}`);
                        setAlertType("info");
                        setShowAlert(true);
                    }
                } else {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch((error) => {
                console.log("View Documents Api: ", error);
                setIsLoading(false);
                if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                const status = error?.response?.status;
                if (status === 403) {
                    triggerSessionExpire();
                } else {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access."
                            : status === 429
                                ? "Request limit exceeded."
                                : "An error occurred while processing your request."
                    );
                    setAlertType("error");
                    setShowAlert(true);
                    if (status === 429) {
                        setTimeout(logOut, 3000);
                    }
                }
            });
    }

    const handleBulkApproval = async (selectedRows) => {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const documentRowData = selectedRows?.map(({
            deal_pfi_id: Deal_PFI_Id,
            tranche_id: Tranche_Id,
            coo_id: COO_ID,
            external_file_id: External_File_Id,
            revision_number: Revision_Number,
            "Document Type": Document_Type,
            "Document Name": Document_Name,
            isApproved,
            is_external_document: isExternalDocument,
            Business_Unit_Id
        }) => ({
            Deal_PFI_Id,
            Tranche_Id,
            COO_ID,
            External_File_Id,
            Revision_Number,
            Document_Type: isExternalDocument ? "External Document" : Document_Type,
            Document_Name,
            Reviewed_By: currentUser?.id,
            Document_Status: isApproved ? 0 : 1,
            Business_Unit_Id
        }));

        try {
            const response = await axios({
                method: "post",
                url: "/api/ApproveDocuments",
                data: documentRowData,
                signal: abortController.signal
            });

            setIsLoading(false);
            const { status } = response;

            if (status === 200) {
                setToggleDisplayData(!toggleDisplayData);
            } else {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
            }
        } catch (error) {
            console.log("Approve Documents Api: ", error);
            setIsLoading(false);
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access."
                        : status === 429
                            ? "Request limit exceeded."
                            : "An error occurred while processing your request."
                );
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        }
    }

    const handleEditDeal = async (data, tab) => {
        const { deal_number_id: dealId, deal_pfi_id: dealPFIId, deal_number: dealNo, "Deal No.": pfiNo, Business_Unit_Id, tranche_id, "Tranche Name": trancheName } = data;

        const businessUnitId = typeof Business_Unit_Id === 'string' ? parseInt(Business_Unit_Id, 10) : Business_Unit_Id;

        // Find the matching business unit
        const matchedBusinessUnit = businessUnits.find(unit =>
            unit.id === businessUnitId
        );

        if (!matchedBusinessUnit) {
            console.error('No matching business unit found for ID:', businessUnitId);
            return;
        }

        let periodLock = data?.period_lock;
        if (tab === 'Pre-Shipment') {
            setIsLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    url: "/api/GetDealPFIPeriodLockStatus",
                    data: {
                        pathname,
                        Business_Unit_Id: businessUnitId,
                        deal_pfi_id: dealPFIId
                    },
                    signal: abortController.signal
                });

                const { status, data: responseData } = response;
                setIsLoading(false);
                if (status === 200) {
                    periodLock = responseData?.period_lock;
                }
            } catch (error) {
                setIsLoading(false);
                console.log("Period Lock Status Field Api: ", error);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                    return;
                }

                const status = error?.response?.status;

                if (status === 403) {
                    triggerSessionExpire();
                    return;
                } else {
                    // Handle errors similarly to the provided structure
                    const errorMessage = status === 401
                        ? "Unauthorized access. You do not have the required permissions to perform this action."
                        : status === 429
                            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                            : "An error occurred while processing your request. Please try again later or contact the site administrator.";

                    console.error(errorMessage);
                    if (status === 429) {
                        setTimeout(logOut, 3000);
                    }
                    // Optionally set alert states if needed
                    // setAlertMessage(errorMessage);
                    // setAlertType("error");
                    // setShowAlert(true);
                    periodLock = false; // Fallback value
                }
            }
        }

        const routePath = `/deals/${matchedBusinessUnit.value.toLowerCase()}`;

        navigate(routePath, {
            state: {
                dealState: {
                    selectedDeal: { id: dealId, value: dealNo },
                    selectedPFI: {
                        id: dealPFIId,
                        value: pfiNo?.slice(dealNo.length),
                        period_lock: periodLock
                    },
                    activeTab: tab,
                    selectedTranche: {
                        id: tranche_id,
                        value: trancheName
                    }
                },
                selectedBusinessUnit: matchedBusinessUnit
            }
        });
    }

    const handleCloseModal = () => {
        setShowCommentModal(false);
        setShowDisableModal(false);
    }

    const handleFilter = () => {
        setFilter(true);
        setToggleDisplayData(!toggleDisplayData);
    }


    const handleReset = () => {
        setSelectedEntity(null);
        setSelectedPaymentOption(paymentOptions[0]);
        setSelectedDocumentStatus(documentStatusOptions[0])
        setIsFilterable(false);
        setFilter(false);
        setToggleDisplayData(!toggleDisplayData);
    }

    return (
        <div className="content-container d-flex flex-column container approve-document">
            <Alert
                show={showAlert}
                message={alertMessage}
                type={alertType}
                setShow={setShowAlert}
            />

            <h4 className="page-heading m-0">
                Approve Documents
            </h4>



            <div className={`${businessUnits?.length > 0 ? 'show-tabs' : 'hide-tabs'}`}>
                <Box className="mb-1" display="flex" flexDirection={'column'} justifyContent="center" alignItems={'center'} width="100%">
                    {businessUnits?.length > 0 && (
                        <Box className="mb-1" display="flex" justifyContent="center" width="100%">
                            <Tabs
                                value={selectedTab}
                                variant="scrollable"
                                scrollButtons="auto"
                                onChange={(event, newValue) => handleTabChange(newValue)}
                                sx={{
                                    minHeight: '38px !important',
                                    marginBottom: '0.5em',
                                    backgroundColor: '#eef2ff',
                                    '& .MuiTab-root': {
                                        minHeight: '38px !important'
                                    }
                                }}
                            >
                                <Tab key="All" label="All" />
                                {businessUnits?.map((businessUnit) => (
                                    <Tab
                                        key={businessUnit.id}
                                        label={businessUnit.value === "SBLC" ? "Polymers - SBLC" : businessUnit.value}
                                    />
                                ))}
                            </Tabs>

                        </Box>
                    )}
                    <Grid container spacing={1} justifyContent="center" alignItems="center" className="mb-2">
                        <Grid size={{ xs: 2 }} className="mass-update-filter autocomplete">
                            <VirtualizedAutocomplete
                                isLoading={isEntitiesLoading}
                                isMultiple={false}
                                isObject={true}
                                isWritable={true}
                                filterOn="Select Entity"
                                options={isEntitiesLoading ? [] : entitiesForCopy}
                                selectedOptions={selectedEntity || null}
                                handleSelectChange={(filterOn, newValue) => setSelectedEntity(newValue)}
                            />
                        </Grid>

                        <Grid size={{ xs: 2 }} className="mass-update-filter autocomplete">
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={false}
                                isWritable={true}
                                filterOn="Only LC Transactions"
                                options={paymentOptions}
                                selectedOptions={selectedPaymentOption}
                                handleSelectChange={(filterOn, newValue) => {
                                    setSelectedPaymentOption(newValue);
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 2 }} className="mass-update-filter autocomplete">
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={false}
                                isWritable={true}
                                filterOn="Document Status"
                                options={documentStatusOptions}
                                selectedOptions={selectedDocumentStatus}
                                handleSelectChange={(filterOn, newValue) => {
                                    setSelectedDocumentStatus(newValue);
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 'auto' }}>
                            <ButtonGroup variant="contained" disableElevation={true}>
                                <Button type="submit" className="filter-button" color="primary" disabled={!isFilterable} onClick={handleFilter}>Filter</Button>
                                <Button className="reset-button" color="info" disabled={!isFilterable} startIcon={<BiReset />} onClick={handleReset}></Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Box>
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <Loader />
                </Backdrop>



                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MaterialReactDataTable
                        ref={tableRef}
                        title="Approve Documents"
                        pageKey={pageKey}
                        isWritable={isWritable}
                        disableComments={false}
                        isTabChanged={isTabChanged}
                        isBusinessUnitSelected={businessUnits?.length > 0}
                        allowBulkActions={true}
                        currentUserId={currentUser?.id}
                        globalFilterValue={globalFilterValue}
                        isDealPFIActionsAllowed={isLCTeamRole || isDocumentTeamRole}
                        isDealPFIStatusChangeAllowed={isLCTeamRole}
                        tableData={documentData}
                        setIsLoading={setIsLoading}
                        setShowAlert={setShowAlert}
                        setAlertMessage={setAlertMessage}
                        setAlertType={setAlertType}
                        setIsTabChanged={setIsTabChanged}
                        setGlobalFilterValue={setGlobalFilterValue}
                        handleGlobalFilter={handleGlobalFilter}
                        handleAddOrEditComment={handleAddOrEditComment}
                        handleViewDocument={handleViewDocument}
                        handleRecordStatusChange={handleRecordStatusChangeWrapper}
                        handleBulkViewOrDownload={handleBulkViewOrDownload}
                        handleBulkApproval={handleBulkApproval}
                        handleEditDeal={handleEditDeal}
                        handleSendNotification={sendAllDocumentsApprovedNotification}
                    />
                </LocalizationProvider>
            </div>

            <Modal show={showDisableModal} onHide={handleCloseModal} centered={true}>
                <Modal.Header style={{ padding: '7px 16px' }} closeButton={true}>
                    <Modal.Title>Are you sure?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span>
                        {rowData?.isDeleted ? 'Deleting' : 'Disapproving'} the document will remove it from the list of {rowData?.isDeleted ? 'accessible' : 'approved'} documents.
                    </span>
                </Modal.Body>
                <Modal.Footer className="gap-2">
                    <Button variant="outlined" size="small" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button variant="contained" size="small" onClick={() => handleRecordStatusChange(rowData)}>
                        {rowData?.isDeleted ? 'Delete' : 'Disapprove'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <DocumentCommentsSection
                show={showCommentModal}
                onHide={handleCloseModal}
                rowCommentData={rowCommentData}
                currentUser={currentUser}
                handleRecordStatusChange={handleRecordStatusChangeWrapper}
                setShowAlert={setShowAlert}
                setAlertMessage={setAlertMessage}
                setAlertType={setAlertType}
                setIsLoading={setIsLoading}
                triggerSessionExpire={triggerSessionExpire}
                logOut={logOut}
            />
        </div>
    );
}

export default withAuth(ApproveDocuments)([
    roles[2],
    roles[3],
    roles[4],
    roles[5],
    roles[6],
    roles[7],
    roles[8],
    roles[10],
    roles[11],
    roles[12],
    roles[13],
    roles[14],
    roles[15],
    roles[16],
    roles[17],
    roles[18],
    roles[19]
]);